import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Box, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { MODULE_STYLES } from './constants';
import ResizeHandle from './ResizeHandle';

export default function ModuleElement({
  element,
  zoom = 1,
  isSelected,
  onClick,
  onDelete,
  onResize,
  onResizeEnd,
  onRotate,
}) {
  const { id, type, label, x, y, width, height, rotation = 0 } = element;
  const style_ = MODULE_STYLES[type] || { bgcolor: '#e0e0e0', color: '#616161', label: '?' };

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `module-${id}`,
    data: { type: 'CANVAS_ELEMENT', id, isTable: false },
  });

  const boxStyle = {
    position: 'absolute',
    left: x + (transform?.x ?? 0) / zoom,
    top: y + (transform?.y ?? 0) / zoom,
    width,
    height,
    cursor: isDragging ? 'grabbing' : 'grab',
    userSelect: 'none',
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 100 : (isSelected ? 10 : 3),
    background: style_.bgcolor,
    border: isSelected ? '2px solid #2a5d32' : '1.5px solid rgba(0,0,0,0.15)',
    borderRadius: 6,
    boxShadow: isSelected ? '0 0 0 3px rgba(42,93,50,0.25)' : '0 2px 4px rgba(0,0,0,0.08)',
    // overflow: visible so resize/rotation handles (outside the element bounds) are accessible
    overflow: 'visible',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 0.5,
    transform: `rotate(${rotation}deg)`,
    transformOrigin: 'center center',
  };

  return (
    <Box
      ref={setNodeRef}
      style={boxStyle}
      data-dnd-draggable="true"
      {...listeners}
      {...attributes}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      {/* Inner wrapper clips content overflow while outer allows handle overflow */}
      <Box sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        borderRadius: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        pointerEvents: 'none',
      }}>
        <Typography sx={{ fontSize: 22, lineHeight: 1 }}>
          {style_.label}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ color: style_.color, textAlign: 'center', px: 0.5, fontSize: 11 }}
        >
          {label}
        </Typography>
      </Box>

      {/* Delete button – outside inner wrapper so it's above the overflow:hidden clip */}
      {isSelected && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          sx={{
            position: 'absolute',
            top: -14,
            right: -14,
            bgcolor: '#f44336',
            color: '#fff',
            width: 20,
            height: 20,
            '&:hover': { bgcolor: '#d32f2f' },
            zIndex: 20,
          }}
        >
          <DeleteIcon sx={{ fontSize: 12 }} />
        </IconButton>
      )}

      {isSelected && (
        <ResizeHandle
          x={x}
          y={y}
          width={width}
          height={height}
          zoom={zoom}
          rotation={rotation}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          onRotate={onRotate}
        />
      )}
    </Box>
  );
}
